import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { HomeContainer, LoadingContainer, TrainingContainer, StyleSamplesContainer } from "./styles";
import InlineSVG from "react-inlinesvg";
import Dropzone from "react-dropzone";
import classNames from "classnames";
import Auth from "../../Auth";
import useMyVoiceStyleSamples from "../../hooks/useMyVoiceStyleSamples";
import { sendAppEvent } from "../../Utils";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { sortBy } from "lodash";
import { openai_fine_tune_statuses } from "@prisma/client";
import { useToast } from "../ToastWrapper";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Text } from "../../sudokit/components/Text";
import ThreeDotsSelect from "../../sudokit/components/SudoSelect/ThreeDotsSelect";

function HomeScreen({ onStartTraining, customModels }) {
  const [file, setFile] = useState();
  const [textInput, setTextInput] = useState("");

  return (
    <HomeContainer>
      <div className="home-instructions">
        <Text
          as="h1"
          className="title"
          size="3xl"
          weight="normal"
          font="gt-super-text"
          align="center"
        >
          A few important notes:
        </Text>

        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <div className="important-notes">
            <ul className="notes-list">
              <li>
                <Text
                  size="xl"
                  color="grey-1"
                  className="note-item"
                >
                  Your writing sample and your Voice will only be available to you.
                </Text>
              </li>
              <li>
                <Text
                  size="xl"
                  color="grey-1"
                  className="note-item"
                >
                  Share at least 1,000 words that match your desired style. A full book is ideal, but as short as a single chapter is OK. There is no word limit.
                </Text>
              </li>
              <li>
                <Text
                  size="xl"
                  color="grey-1"
                  className="note-item"
                >
                  Only train on your own work!
                </Text>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="home-inputs">
        <Dropzone
          aria-label="import"
          accept={{
            "text/plain": [".txt"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/msword": [".doc"],
          }}
          maxFiles={1}
          onDrop={async (acceptedFiles) => {
            setFile(acceptedFiles[0]);
            setTextInput(""); // Clear text input when file is uploaded
          }}
          onDropRejected={() => {
            alert("Too many files - select a single .txt or .docx file");
          }}
        >
          {({ getRootProps, getInputProps, isDragReject, isDragAccept }) =>
            file ? (
              <div className="upload complete">
                <InlineSVG
                  src="/assets/my-voice.svg"
                  width="4em"
                  height="4em"
                />
                <Text
                  className="title"
                  size="lg"
                  weight="medium"
                  align="center"
                  style={{ margin: "0 1rem" }}
                >
                  Upload received, click "Start Training" below to begin
                </Text>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={classNames({ upload: true, rejected: isDragReject, accepted: isDragAccept })}
              >
                <input {...getInputProps()} />
                <InlineSVG
                  src="/assets/import.svg"
                  width="2em"
                  height="2em"
                />
                <Text
                  className="title"
                  size="lg"
                  weight="medium"
                  align="center"
                >
                  Drag your Book here
                </Text>
                <Text
                  className="supported-format"
                  size="sm"
                  color="grey-1"
                  align="center"
                >
                  (.txt or .docx format)
                </Text>
                <Text
                  className="upload-link"
                  size="sm"
                  align="center"
                >
                  or <a>click here to browse</a>
                </Text>
              </div>
            )
          }
        </Dropzone>

        <div className="textarea-container">
          <textarea
            placeholder="Or paste your writing sample here..."
            value={textInput}
            onChange={(e) => {
              setTextInput(e.target.value);
              if (e.target.value.trim() && file) {
                setFile(undefined); // Clear file when text is entered
              }
            }}
          />
        </div>
      </div>

      <div className="home-actions">
        <SudoButton
          variant="primary"
          disabled={!file && !textInput.trim()}
          onClick={() => onStartTraining(file, textInput)}
        >
          Start Training
        </SudoButton>
      </div>
    </HomeContainer>
  );
}

function isTraining(model) {
  switch (model.status) {
    case openai_fine_tune_statuses.failed:
    case openai_fine_tune_statuses.succeeded:
    case openai_fine_tune_statuses.cancelled:
      return false;
    default:
      return true;
  }
}

const TRAINING_TIME = 30;

function getProgression(model) {
  const fromElapsed = (elapsed) => (elapsed / TRAINING_TIME) * 100.0;

  switch (model.status) {
    case openai_fine_tune_statuses.preprocessing:
      return {
        status: "running",
        text: "Preprocessing",
        progression: fromElapsed(0),
      };
    case openai_fine_tune_statuses.preprocessing_succeeded:
      return {
        status: "running",
        text: "Uploading files",
        progression: fromElapsed(10),
      };
    case openai_fine_tune_statuses.uploaded_files:
      return {
        status: "running",
        text: "In Training Queue",
        progression: fromElapsed(15),
      };
    case openai_fine_tune_statuses.queued:
      return {
        status: "running",
        text: "Waiting for training to start",
        progression: fromElapsed(15),
      };
    case openai_fine_tune_statuses.running:
      return {
        status: "running",
        text: "Learning your voice",
        progression: fromElapsed(15),
      };
    case openai_fine_tune_statuses.cancelled:
      return {
        status: "failed",
        text: "Cancelled",
        progression: fromElapsed(20),
      };
    case openai_fine_tune_statuses.failed:
    case openai_fine_tune_statuses.preprocessing_failed:
      return {
        status: "failed",
        text: "Failed",
        progression: fromElapsed(20),
      };
    case openai_fine_tune_statuses.succeeded:
      return {
        status: "success",
        text: "Success",
        progression: fromElapsed(30),
      };
    default:
      return {
        status: "running",
        text: "Training",
        progression: 70,
      };
  }
}

function LoadingScreen() {
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowError(true);
    }, 4000);

    return () => clearTimeout(timeout);
  }, []);

  if (!showError) {
    return null;
  }

  return (
    <LoadingContainer>
      <p>Loading... If it's taking too long please come back later.</p>
    </LoadingContainer>
  );
}

function StyleSampleItem({ sample, onUpdate }) {
  const confirmAction = useConfirmation(sample.sample_id);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(sample.name);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    if (!editedName.trim()) {
      setEditedName(sample.name); // Reset to original name
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    try {
      await Auth.fetchWithIdToken("/api/my_voice/update_style_sample", {
        sample_id: sample.sample_id,
        name: editedName.trim(),
      });
      onUpdate(); // Refresh the list
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating sample name:", error);
      setEditedName(sample.name); // Reset to original name
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditedName(sample.name);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = async () => {
    await confirmAction({
      title: "Delete Voice",
      message: "Are you sure you want to delete this Voice?",
      actionLabel: "Delete Voice",
      destructive: true
    }, async () => {
      setIsUpdating(true);
      try {
        await Auth.fetchWithIdToken("/api/my_voice/delete_style_sample", {
          sample_id: sample.sample_id
        });
        onUpdate(); // Refresh the list
      } catch (error) {
        console.error("Error deleting sample:", error);
        alert("Failed to delete sample. Please try again.");
      } finally {
        setIsUpdating(false);
      }
    });
  };

  return (
    <div className="style-sample-item">
      {isEditing ? (
        <div className="editable-name">
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={handleSave}
            autoFocus
            disabled={isUpdating}
          />
        </div>
      ) : (
        <>
          <span className="sample-name">{sample.name}</span>
          <div className="menu-container">
            <ThreeDotsSelect
              label="Style sample options"
              optionAlignment="left"
              options={[
                {
                  value: "edit",
                  label: "Edit Name",
                  icon: {
                    label: "Edit name",
                    src: "/assets/pencil.svg",
                    status: "default",
                  },
                },
                {
                  value: "delete",
                  label: "Delete",
                  icon: {
                    label: "Delete sample",
                    src: "/assets/delete.svg",
                    status: "danger",
                  },
                },
              ]}
              onChange={(value) => {
                if (value === "edit") {
                  handleEdit();
                } else if (value === "delete") {
                  handleDelete();
                }
              }}
              disabled={isUpdating}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MyVoiceTrainingScreen({ onComplete }) {
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Training checklist items
  const checklistItems = ["Language", "Reading level", "Perplexity", "POV & Tense", "Turns of phrase", "Characterization patterns", "Sentence length", "Dialogue style", "Word choice", "Vibes"];

  const fetchTrainingStatus = useCallback(async () => {
    try {
      const response = await Auth.fetchWithIdToken("/api/my_voice/training_status");
      setTrainingStatus(response);

      if (response.isComplete) {
        // Training is complete, show for a moment then transition
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (error) {
      console.error("Error fetching training status:", error);
    } finally {
      setLoading(false);
    }
  }, [onComplete]);

  useEffect(() => {
    fetchTrainingStatus();

    // Poll training status every 2 seconds
    const interval = setInterval(fetchTrainingStatus, 2000);
    return () => clearInterval(interval);
  }, [fetchTrainingStatus]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!trainingStatus || !trainingStatus.isTraining) {
    onComplete();
    return null;
  }

  // Calculate which items should be shown as complete based on elapsed time
  const progressPercentage = Math.min(100, (trainingStatus.elapsedMs / trainingStatus.totalMs) * 100);
  const itemsToShow = Math.min(checklistItems.length, Math.ceil((progressPercentage / 100) * checklistItems.length));
  const completedItems = Math.max(0, itemsToShow - 1);

  return (
    <TrainingContainer>
      <div className="training-container-inner">
        <InlineSVG
          src="/assets/my-voice.svg"
          width="4em"
          height="4em"
        />
        <Text
          className="title"
          size="xl"
          font="gt-super-text"
          weight="normal"
          color="grey-0"
          align="center"
        >
          Training Your Voice
        </Text>
        <Text
          size="md"
          color="grey-1"
          align="center"
          weight="bold"
        >
          While you wait,{" "}
          <a
            href="https://sudowrite.notion.site/My-Voice-Beta-Participant-Notes-2247975fa0ec805fb4b5d19c0a936589?pvs=74"
            target="_blank"
          >
            click here to read the short My Voice (Beta) Guide
          </a>
          .
        </Text>
        <div className="training-checklist">
          {[...checklistItems].reverse().map((item, reverseIndex) => {
            const originalIndex = checklistItems.length - 1 - reverseIndex;
            const isVisible = originalIndex < itemsToShow;
            const isComplete = originalIndex < completedItems;
            const isProcessing = originalIndex === completedItems && isVisible;

            if (!isVisible) return null;

            return (
              <div
                key={originalIndex}
                className={`checklist-item ${isComplete ? "complete" : ""} ${isProcessing ? "processing" : ""}`}
              >
                <div className="status-icon">
                  {isComplete ? (
                    <InlineSVG
                      src="/assets/check-simple.svg"
                      width="1.2em"
                      height="1.2em"
                    />
                  ) : (
                    <InlineSVG
                      src="/assets/loader.svg"
                      width="1.2em"
                      height="1.2em"
                      className="spinning"
                    />
                  )}
                </div>
                <span className="item-text">{item}</span>
              </div>
            );
          })}
        </div>

        <div className="progress-info">
          <p>
            Estimated time remaining: {Math.ceil(trainingStatus.remainingMs / 1000 / 60)} minute{Math.ceil(trainingStatus.remainingMs / 1000 / 60) !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
    </TrainingContainer>
  );
}

function StyleSamplesScreen({ onTrainNew }) {
  const { styleSamples: samples, isLoading: loading, refetchStyleSamples } = useMyVoiceStyleSamples();

  // Track when user first sees StyleSamplesScreen with no samples (approximates first time)
  useEffect(() => {
    if (!loading && samples.length === 0) {
      sendAppEvent("My Voice Screen - First View", {
        samples_count: 0,
      });
    }
  }, [loading, samples.length]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <StyleSamplesContainer>
      <div className="header">
        <h1 className="title">My Voice (Beta)</h1>
        <p className="description">
          <span className="description-highlight">My Voice makes Sudowrite sound more like you.</span> First, it studies your writing sample to learn how you write. Then, it trains a custom Voice to
          mimic your writing style in Sudowrite.
        </p>
      </div>

      <div className="samples-list">
        {samples.length === 0 ? (
          <div className="empty-state">
            <SudoButton
              variant="primary"
              onClick={onTrainNew}
            >
              Create New Voice
            </SudoButton>
          </div>
        ) : (
          samples.map((sample) => (
            <StyleSampleItem
              key={sample.sample_id}
              sample={sample}
              onUpdate={refetchStyleSamples}
            />
          ))
        )}
      </div>

      {samples.length > 0 && (
        <div className="actions">
          <SudoButton
            variant="primary"
            onClick={onTrainNew}
          >
            Create New Voice
          </SudoButton>
        </div>
      )}
    </StyleSamplesContainer>
  );
}

export default function MyVoice({ user }) {
  const [loading, setLoading] = useState(true);
  const [showHomeScreen, setShowHomeScreen] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const { setToast } = useToast();
  const location = useLocation();
  const history = useHistory();

  // Capture the originating location from navigation state
  const returnPath = location.state?.from;

  // Use SWR hook for style samples
  const { refetchStyleSamples } = useMyVoiceStyleSamples();

  // Keep old training system for backward compatibility
  const fetchCustomModels = useSudowriteStore((state) => state.fetchCustomModels);
  const customModels = useSudowriteStore((state) => state.customModels);

  const checkTrainingStatus = useCallback(async () => {
    try {
      const response = await Auth.fetchWithIdToken("/api/my_voice/training_status");
      setTrainingStatus(response);
    } catch (error) {
      console.error("Error fetching training status:", error);
    }
  }, []);

  const fetchSamples = useCallback(async () => {
    try {
      await checkTrainingStatus();
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, [checkTrainingStatus]);

  useEffect(() => {
    fetchSamples();
    // Still fetch custom models for backward compatibility
    fetchCustomModels();
  }, [fetchSamples, fetchCustomModels]);

  const lastModel = sortBy(customModels, "createdAt").reverse()[0];

  // Show old training screen if there's an active training model
  useEffect(() => {
    if (lastModel && isTraining(lastModel)) {
      setShowHomeScreen(false);
    }
  }, [lastModel]);

  if (loading) {
    return <LoadingScreen />;
  }

  // Show home screen for upload
  if (showHomeScreen) {
    return (
      <HomeScreen
        customModels={customModels}
        onStartTraining={async (file, textInput) => {
          setLoading(true);
          setShowHomeScreen(false);

          const formData = new FormData();
          if (file) {
            formData.append("file", file);
          }
          if (textInput) {
            formData.append("textInput", textInput);
          }

          sendAppEvent("My Voice Training Started");

          try {
            await Auth.postFilesWithIdToken("/api/my_voice/process_user_style_sample", { params: { invocationType: "my-voice-training" } }, formData);
            // Start checking training status
            fetchSamples();
          } catch (error) {
            console.error("Error processing style sample:", error);

            // Extract error message from response if available
            let errorMessage = "Error processing style sample. Please try again.";

            // The Auth.postFilesWithIdToken throws { error: "Server problem.", response: response }
            // We need to parse the response body to get the actual server error
            if (error.response) {
              try {
                const responseData = await error.response.json();
                if (responseData.error) {
                  errorMessage = responseData.error;
                }
              } catch (parseError) {
                console.error("Error parsing response:", parseError);
                // Fallback to generic message
              }
            } else if (error.error && error.error !== "Server problem.") {
              errorMessage = error.error;
            } else if (error.message && error.message !== "Bad Request") {
              errorMessage = error.message;
            }

            setToast(errorMessage);
            setLoading(false);
          }
        }}
      />
    );
  }

  // Show new training screen if My Voice training is active
  if (trainingStatus && trainingStatus.isTraining) {
    return (
      <MyVoiceTrainingScreen
        onComplete={() => {
          setTrainingStatus(null);
          refetchStyleSamples(); // Refresh the SWR cache to show the new sample
          fetchSamples();

          // Navigate back to the project if user came from a project
          if (returnPath) {
            history.push(returnPath);
          }
        }}
      />
    );
  }

  // Default to showing style samples screen
  return <StyleSamplesScreen onTrainNew={() => setShowHomeScreen(true)} />;
}
